/**
 * PatientImportListPage Component
 *
 * Smart page for /patients/import:
 * - If sessions exist → master-detail layout with active/complete tabs
 * - If no sessions → renders the upload form inline
 * - Loading → spinner
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubjectLanguage } from '@features/user';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { capitalize } from '@utils/utils';

import { useImportSessionsList } from '../hooks';

import { PatientImportUploadPage } from './PatientImportUploadPage';
import { SessionDetailPanel } from './SessionDetailPanel';
import { SessionList } from './SessionList';

// =============================================================================
// Component
// =============================================================================

export const PatientImportListPage: React.FC = () => {
  const navigate = useNavigate();
  const subjectLanguage = useSubjectLanguage();
  const { patientImport } = useEventTracking();
  const [showUpload, setShowUpload] = useState(false);
  const sessionsViewedRef = useRef(false);

  const {
    activeTab,
    setActiveTab,
    sessions,
    activeCount,
    completeCount,
    isLoading,
    hasSessions,
    selectedSessionId,
    selectSession,
    deleteSession,
    isDeleting,
  } = useImportSessionsList();

  // Track sessions viewed once when data loads
  useEffect(() => {
    if (hasSessions && !sessionsViewedRef.current) {
      sessionsViewedRef.current = true;
      patientImport.sessionsViewed({
        active_count: activeCount,
        complete_count: completeCount,
      });
    }
  }, [hasSessions, activeCount, completeCount, patientImport]);

  // Still loading both queries — show spinner
  if (hasSessions === undefined) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 10,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // No sessions at all — show the upload form
  if (!hasSessions && !showUpload) {
    return <PatientImportUploadPage />;
  }

  // User clicked "New Import" — show upload form with back option
  if (showUpload) {
    return <PatientImportUploadPage />;
  }

  // Sessions exist — master-detail layout
  const selectedSession = sessions.find(
    (s) => s.session_id === selectedSessionId,
  );

  return (
    <Box
      sx={{
        maxWidth: 1200,
        mx: 'auto',
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => navigate('/patients')}
            aria-label="Back to patients"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight={600}>
            Import {capitalize(subjectLanguage)}s
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowUpload(true)}
        >
          New Import
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v: 'active' | 'complete') => setActiveTab(v)}
        sx={{ mb: 2 }}
      >
        <Tab value="active" label={`Active (${activeCount})`} />
        <Tab value="complete" label={`Complete (${completeCount})`} />
      </Tabs>

      {/* Master-detail layout */}
      <Paper
        variant="outlined"
        sx={{
          display: 'flex',
          height: 'calc(100vh - 260px)',
          minHeight: 300,
          overflow: 'hidden',
          borderRadius: 2,
        }}
      >
        {/* Left panel — session list (scrollable) */}
        <Box
          sx={{
            width: 340,
            minWidth: 340,
            borderRight: 1,
            borderColor: 'divider',
            overflowY: 'auto',
          }}
        >
          <SessionList
            sessions={sessions}
            selectedSessionId={selectedSessionId}
            onSelect={selectSession}
            isLoading={isLoading}
          />
        </Box>

        {/* Right panel — session detail */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <SessionDetailPanel
            session={selectedSession}
            onDelete={deleteSession}
            isDeleting={isDeleting}
          />
        </Box>
      </Paper>
    </Box>
  );
};
